/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { Button } from 'reactstrap';
import Logo from '../../images/logo.svg';
import Loader from '../../components/loader';
import Footer from '../../components/footer';
import { loadOnboardingToken, reloadOnboardingToken } from '../../actions/onboardingToken';
import * as auth from '../../actions/auth';
import { loadCurrentUser } from '../../actions/user';
import UserForm from './forms/user';
import SurveyForm from './forms/survey';
import Error from './forms/error';
import ActivationRequired from './forms/ActivationRequired';
import queryString from 'query-string';
import { loadCommon } from '../../actions/common';
import { getCountries } from '../../selectors/common';
import { showLogin } from '../../actions/login';
import { ROUTES } from '../../constants/routes';
import { generateUrl } from '../util';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { OnboardingStatus, OnboardingToken } from '../../types/onboarding';
import { RootState } from '../../reducers';
import './styles.scss';
import { getOnboardingAppPath } from '../../selectors/appConfig';
import { hasAssuranceConfig } from '@utils/onboarding';

class Onboarding extends React.Component<PropsFromRedux & RouteComponentProps> {
  static UserForm = 'user';
  static UserComplete = 'user_complete';
  static SurveyForm = 'survey';
  static Error = 'error';

  state = {
    onboardingForm: undefined,
    userMessage: '',
    action: undefined,
  };

  reloadOnboarding = () => this.props.reloadOnboardingToken(this.props.token);

  setUserMessage = (message = '') => this.setState({ userMessage: message });

  componentDidMount() {
    this.props.loadOnboardingToken(this.props.token);
    this.props.loadCurrentUser();
    this.props.loadCommon();
  }

  componentDidUpdate(prevProps: PropsFromRedux) {
    const { currentUser } = this.props;

    if (prevProps.currentUser.data._id !== currentUser.data._id) {
      // User have been just loaded - reload token and block with loading
      // This will ensure onboarding is still valid after potential provisioning
      this.props.loadOnboardingToken(this.props.token);
    }

    const onboardingForm = this.getForm();
    const onboardingToken = this.props.onboardingTokenState.data as (Partial<OnboardingToken> | OnboardingToken);

    if (onboardingForm === Onboarding.UserComplete && currentUser.data.active === true) {
      if (hasAssuranceConfig(onboardingToken)) {
        // Go to assurance as priority
        return this.props.history.push(generateUrl(ROUTES.ASSURANCE));
      }

      const rootAppPath = this.props.onboardingAppPath;
      if (onboardingToken.initiativeId) {
        return this.props.history.push(generateUrl(ROUTES.SUMMARY, { rootAppPath, initiativeId: onboardingToken.initiativeId }));
      }

      return this.props.history.push(generateUrl(ROUTES.COMPANY_TRACKER, { rootAppPath }));
    }

    const prevToken = prevProps.onboardingTokenState.data;

    if (onboardingToken._id && onboardingToken._id !== prevToken._id) {
      const onboardUser = (onboardingToken as OnboardingToken).user;
      if (!onboardUser.complete) {
        const analytics = getAnalytics();
        analytics.track(AnalyticsEvents.UserInvitationClick, {
          initiativeId: onboardingToken.initiativeId,
          onboardId: onboardingToken._id
        });
      }
    }
  }

  getForm() {
    const { onboardingTokenState, loggedIn } = this.props;

    if (onboardingTokenState.errored || !onboardingTokenState.loaded) {
      if (!this.state.action) {
        return Onboarding.Error;
      }
    }

    const token = onboardingTokenState.data;

    if (token.status === OnboardingStatus.Complete) {
      return Onboarding.UserComplete;
    }

    const onboardUser = token.user;
    const userOnboardingRequired = !loggedIn && onboardUser && !onboardUser.complete;

    if (userOnboardingRequired) {
      return Onboarding.UserForm;
    }

    if (this.isSurveyOnboardingRequired(token)) {
      return Onboarding.SurveyForm;
    }

    if (onboardUser && onboardUser.complete) {
      return Onboarding.UserComplete;
    }

    return Onboarding.Error;
  }

  isSurveyOnboardingRequired(token: OnboardingToken) {
    return (token.surveyConfig && !token.surveyConfig.complete) ||
      token.surveyStakeholders ||
      token.utrvConfig ||
      token.assuranceStakeholders ||
      token.surveyRoles ||
      token.delegationScope
      ;
  }

  renderLoggedInMessage() {
    const { currentUser, handleLogout } = this.props;
    if (!currentUser.loaded) {
      return;
    }
    const user = currentUser.data;
    return <div className='row'>
      <div className='offset-3 col-6'>
        <p>You are currently logged in as <strong>{user.firstName} {user.surname}</strong>.
          To use a different user <span className={'ml-2 link-like text-nowrap'} onClick={() => handleLogout()}> click here.</span>
        </p>
      </div>
    </div>;
  }

  render() {
    const { onboardingTokenState, currentUser, countries } = this.props;
    const { userMessage } = this.state;

    const currentUserCompleted = !currentUser.loading || currentUser.loaded || currentUser.errored;

    if ((!onboardingTokenState.loaded && !onboardingTokenState.errored) || !currentUserCompleted) {
      return (<Loader />);
    }
    const onboardingForm = this.getForm();

    const token = onboardingTokenState.data;
    let loginRequired;
    switch (onboardingForm) {
      case Onboarding.UserForm:
      case Onboarding.Error:
        loginRequired = false;
        break;
      default:
        loginRequired = true;
        break;
    }

    const status = token.status === OnboardingStatus.Complete ? 'has been completed' : 'is pending';

    return <div className='onboardingWrapper'>
      <div className='onboardingContainer'>
        <header>
          <img className='logo' src={Logo} alt='G17Eco' />
        </header>

        <div className='onboardingBodyContainer'>
          {this.renderLoggedInMessage()}
          {userMessage &&
            <div className='row mt-3'>
              <div className='offset-3 col-6'>
                <div className='alert alert-primary' dangerouslySetInnerHTML={{ __html: userMessage }} />
              </div>
            </div>
          }
          {onboardingForm === Onboarding.UserForm &&
            <UserForm
              reloadOnboarding={this.reloadOnboarding}
              countries={countries}
              token={token}
              setUserMessage={this.setUserMessage}
              showLogin={(path) => this.onLogin(path)} />
          }
          {currentUser && currentUser.loaded && onboardingForm === Onboarding.SurveyForm &&
            <SurveyForm reloadOnboarding={this.reloadOnboarding} token={token} setUserMessage={this.setUserMessage} />
          }
          {onboardingForm === Onboarding.UserComplete && currentUser.data.active === false &&
            <ActivationRequired />
          }
          {onboardingForm === Onboarding.Error &&
            <Error reloadOnboarding={this.reloadOnboarding} token={token} setUserMessage={this.setUserMessage} />
          }

          {!currentUser.loaded && loginRequired &&
            <div className={'mt-5 row'}>
              <div className={'offset-3 col-6'}>
                <h1>Welcome!</h1>
                <p>This invitation {status}. Please log in using the button below.</p>
                <div className='text-right'>
                  <Button
                    type='button'
                    onClick={() => this.onLogin()}
                    name={'action'}
                    value={'show'}
                    color={'secondary'}
                    className='w-100'>
                    {token.externalIdp ? 'Please login with your SSO provider' : 'Log in'}
                </Button>
                </div>
              </div>
            </div>
          }
        </div>
        <Footer />
      </div>
    </div>
  }

  private onLogin(path?: string) {
    const { pathname, search } = this.props.location;
    return showLogin(path ?? pathname + search);
  }
}

const mapStateToProps = (state: RootState, ownProps: RouteComponentProps) => ({
  token: queryString.parse(ownProps.location.search)['invitation_token'] as string,
  onboardingTokenState: state.onboardingToken,
  currentUser: state.currentUser,
  loggedIn: state.currentUser.loaded,
  onboardingAppPath: getOnboardingAppPath(state),
  countries: getCountries(state)
});

const mapDispatchToProps = ({
  loadOnboardingToken,
  reloadOnboardingToken,
  loadCurrentUser,
  handleLogout: auth.logout,
  loadCommon,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>

const component = connector(Onboarding);
export default withRouter(component);
